import React from 'react'
import clsx from 'clsx'
import { cloneDeep } from 'lodash'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'

import CustomFieldInput from '../../../components/molecules/CustomFieldInput'
import CustomFieldTable from '../../../components/molecules/CustomFieldTable'
import CustomFieldFlow from '@SonarCX/components/molecules/CustomFieldFlow'

import { chunkArray } from '../../../utilities'
import { selectCustomFields } from '../../../slices/customFields'
import { selectTaskMetaCache, loadMeta } from '../../../slices/taskMetaCache'
import useCustomFieldValues from '@SonarCX/hooks/useCustomFieldValues'
import useMetaStyles from '../../../components/molecules/ChatModals/TaskModal/TaskMetaSection/styles'
import useDetailsStyles from '../../../components/molecules/ChatModals/TaskModal/styles/TaskDetails'
import useCustomFieldGroups from '../../../hooks/useCustomFieldGroups'
import useStyles from '../CombinedTaskMeta/styles'

type CustomFieldsSectionProps = {
  id: any
  setParentMeta: any
  setChanged: any
  setModifiedTask: any
  canEdit: any
  onBlur?: () => void
  type: string
}

const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  id,
  setParentMeta,
  setChanged,
  setModifiedTask,
  canEdit,
  onBlur = () => {},
  type
}) => {
  const dispatch = useAppDispatch()

  const { customFields } = useAppSelector(selectCustomFields)
  const { loading, loaded, cache } = useAppSelector(selectTaskMetaCache)
  const { groups, hasFields } = useCustomFieldGroups('task', type)

  const [meta, setMeta] = React.useState<APIMeta[]>([])
  const [metaRows, setMetaRows] = React.useState<APIMeta[]>([])

  const taskID = React.useMemo(() => id, [id])

  const onValueChange = React.useCallback(() => {
    setModifiedTask(false)
    setChanged(true)
  }, [setModifiedTask, setChanged])

  const { handleValueChange } = useCustomFieldValues({
    meta,
    setMeta,
    onValueChange
  })

  const metaClasses = useMetaStyles({ modal: false })
  const detailsClasses = useDetailsStyles()
  const classes = useStyles()

  // Loads meta
  React.useEffect(() => {
    if (!taskID) {
      return
    }

    if (!loading.includes(taskID) && !loaded.includes(taskID)) {
      dispatch(loadMeta(taskID))
    }
  }, [loading, loaded, taskID, dispatch])

  // Update fields from cache
  React.useEffect(() => {
    if (taskID in cache) {
      setMeta(cloneDeep(cache[taskID]))
    }
  }, [cache, taskID])

  // Update parents meta
  React.useEffect(() => {
    setParentMeta(meta)
  }, [setParentMeta, meta])

  // Clean mock rows (meta rows) on data insert.
  React.useEffect(() => {
    if (metaRows.length === 0) {
      return
    }

    const tableIds = [...new Set(metaRows.map(mr => mr.key))]

    for (const tableId of tableIds) {
      const mockRows = metaRows.filter(x => x.key === tableId)
      const mockRowsRow = mockRows[0]?.row
      const tableOptions = customFields.find(field => field.id === tableId)

      if (
        meta
          .filter(
            x =>
              x.row === mockRowsRow &&
              x.key === tableId &&
              (tableOptions.options
                ? tableOptions.options.some(opt => opt === x.row_key)
                : (tableOptions.config?.options ?? []).some(
                    opt => opt.val === x.row_key
                  ))
          )
          .some(y => y.value !== '')
      ) {
        setMetaRows(old => {
          const cleanedRows = old.filter(o => o.key !== tableId)
          return cleanedRows
        })
      }
    }
  }, [meta, metaRows, customFields])

  if (!hasFields) {
    return null
  }

  return (
    <div className={clsx(metaClasses.root, classes.root, classes.groupBorder)}>
      {groups.map((group, i) => {
        const fields = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (['table', 'flow'].includes(field.field_type)) {
              return null
            }

            return (
              <div key={field.id} className={detailsClasses.gridItem}>
                <CustomFieldInput
                  field={field}
                  value={meta.find(data => data.key === field.id)?.value ?? ''}
                  disabled={!canEdit}
                  canEdit={canEdit}
                  handleChange={handleValueChange}
                  handleBlur={onBlur}
                  meta={meta}
                  task
                  shouldFilter
                />
              </div>
            )
          })
          .filter(Boolean)

        const tables = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (field.field_type !== 'table') {
              return null
            }

            return field
          })
          .filter(Boolean)

        const flows = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (field.field_type !== 'flow') {
              return null
            }

            return field
          })
          .filter(Boolean)

        const splitPoint = Math.ceil(fields.length / 2)
        const columns = chunkArray(fields, splitPoint)

        if (fields.length + tables.length + flows.length === 0) {
          return null
        }

        return (
          <React.Fragment key={`${group.id}-${i}`}>
            <div className={clsx(classes.group, 'cf-group')}>
              <div className={clsx(classes.title, 'cf-title')}>
                <span>{group.title.toLocaleUpperCase()}</span>
                {group.description && <span>{group.description}</span>}
              </div>

              {fields.length > 0 && (
                <div className={classes.gridContainer}>
                  {columns.map((column, i) =>
                    column.map((item, j) => (
                      <div
                        key={`${i}-${j}`}
                        style={{
                          gridColumn: `${i + 1} / ${i + 2}`,
                          gridRow: `${j + 1} / ${j + 2}`
                        }}
                      >
                        {item}
                      </div>
                    ))
                  )}
                </div>
              )}

              {tables.length > 0 && (
                <div style={{ paddingBottom: 4, overflowY: 'auto' }}>
                  {tables.map(table => (
                    <CustomFieldTable
                      key={table.id}
                      table={table}
                      meta={meta}
                      setMeta={setMeta}
                      metaRows={metaRows}
                      setMetaRows={setMetaRows}
                      canEdit={canEdit}
                      handleValueChange={handleValueChange}
                      onBlur={onBlur}
                      onValueChange={onValueChange}
                    />
                  ))}
                </div>
              )}
            </div>

            {flows.length > 0 && (
              <>
                {flows.map(flow => {
                  return (
                    <CustomFieldFlow
                      meta={meta}
                      setMeta={setMeta}
                      key={flow.id}
                      flow={flow.config?.id}
                      canEdit={canEdit}
                      objectType={'task'}
                      readOnly={!canEdit}
                      handleChange={handleValueChange}
                      handleBlur={onBlur}
                    />
                  )
                })}
              </>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default CustomFieldsSection
